import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Fab,
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Badge,
  Chip,
  Zoom,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { motion, AnimatePresence } from "framer-motion";
import { useProperty } from "../context/PropertyContext";
import { useLocation } from "react-router-dom/dist";

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

const COMPANY_assistantImage = "/assets/images/logo-cccbr.png";
const DEFAULT_assistantImage = "/assets/images/ARI.png";

// Límite de caracteres para mostrar "Ver más"
const DESCRIPTION_CHAR_LIMIT = 150;

const ChatBubble: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXsMobile = useMediaQuery("(max-width:360px)");

  const location = useLocation();

  // Si la ruta contiene /company/ significa que estamos dentro de una compañía
  const isCompanyRoute = location.pathname.startsWith("/company/");

  const assistantImage = isCompanyRoute ? COMPANY_assistantImage : DEFAULT_assistantImage;

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content:
        "Hola, soy ALI, tu Asistente de Alien Realty. ¿Qué tipo de propiedad estás buscando? 🏡",
      role: "assistant",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showGreeting, setShowGreeting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<string, boolean>
  >({});
  const {
    isOpenChatBubble,
    setIsOpenChatBubble,
    searchTermForAri,
    isTextFromSearch,
    setIsTextFromSearch,
  } = useProperty();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesEndRef]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    let greetingTimer: NodeJS.Timeout;

    if (!isOpenChatBubble) {
      greetingTimer = setTimeout(() => {
        setShowGreeting(true);
      }, 2000);

      // Ocultar el saludo después de 8 segundos
      const hideTimer = setTimeout(() => {
        setShowGreeting(false);
      }, 10000);

      return () => {
        clearTimeout(greetingTimer);
        clearTimeout(hideTimer);
      };
    } else {
      setShowGreeting(false);
    }
  }, [isOpenChatBubble]);

  const truncateDescription = useCallback(
    (description: string, propertyId: string) => {
      if (
        description.length <= DESCRIPTION_CHAR_LIMIT ||
        expandedDescriptions[propertyId]
      ) {
        return description;
      }

      return description.substring(0, DESCRIPTION_CHAR_LIMIT) + "...";
    },
    [expandedDescriptions]
  );

  const handleToggleDescription = useCallback(
    (propertyId: string) => {
      setExpandedDescriptions((prev) => ({
        ...prev,
        [propertyId]: !prev[propertyId],
      }));

      setTimeout(scrollToBottom, 100);
    },
    [scrollToBottom]
  );

  const handleSendMessage = useCallback(async () => {
    const inputToUse = isTextFromSearch ? searchTerm : input;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputToUse,
      role: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      /* const response = await fetch(
        "https://alienria-dudrgqgwfbhcdwee.canadacentral-01.azurewebsites.net/searchPropertiesIAPowered",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: input }),
        },
      )*/
      const response = await fetch(
        "https://alienrealtyaria-eyfta0gehdddc7ha.canadacentral-01.azurewebsites.net/searchPropertiesIAPowered",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: inputToUse }),
        }
      );
      const data = await response.json();

      if (Array.isArray(data)) {
        const fullResponse = data
          .map((property, index) => {
            const propertyId = `property-${Date.now()}-${index}`;
            const description = property.description;
            const isLongDescription =
              description.length > DESCRIPTION_CHAR_LIMIT;
            const truncatedDescription =
              property.link && property.photos.length > 0
                ? truncateDescription(description, propertyId)
                : description;

            const link = property.link
              ? `<a href="${
                  property.link
                }" target="_blank" style="color: #4CAF50; text-decoration: none; font-weight: bold; font-size: ${
                  isXsMobile ? "0.75rem" : "0.8rem"
                };">🔗 Ver propiedad</a>`
              : "";

            // Hacer las imágenes responsivas
            const photos = property.photos
              ?.map(
                (photo: any) =>
                  `<img src="${photo}" alt="Imagen propiedad" style="max-width: 100%; height: auto; border-radius: 6px; margin: 4px 0; display: block;"/>`
              )
              .join("");

            // Añadir botón "Ver más" si la descripción es larga
            const viewMoreButton = isLongDescription
              ? `<button 
                  onclick="window.toggleDescription('${propertyId}')" 
                  style="background: none; border: none; color: #4CAF50; cursor: pointer; font-size: ${
                    isXsMobile ? "0.75rem" : "0.8rem"
                  }; padding: 0; margin-top: 4px; font-weight: 500;"
                >
                  ${expandedDescriptions[propertyId] ? "Ver menos" : "Ver más"}
                </button>`
              : "";

            return `
              <div style="border: 1px solid #e0e0e0; padding: ${
                isXsMobile ? "10px" : "12px"
              }; margin-bottom: 12px; border-radius: 10px; background-color: white; box-shadow: 0 2px 4px rgba(0,0,0,0.1); word-break: break-word;" id="${propertyId}">
  <h3 style="margin: 0 0 8px 0; color: #2E7D32; font-size: ${
    isXsMobile ? "0.85rem" : "0.9rem"
  };">${property.title}</h3>

  <p style="margin: 0 0 8px 0; color: #333; font-size: ${
    isXsMobile ? "0.75rem" : "0.8rem"
  };">${truncatedDescription}</p>

  ${viewMoreButton}

  ${
    photos && photos.length > 0
      ? `<div style="margin: 8px 0;">${photos}</div>`
      : ""
  }

  ${link ? `<div style="margin-top: 8px;">${link}</div>` : ""}
</div>
            `;
          })
          .join("");

        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: fullResponse,
          role: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botResponse]);

        // Exponer la función de toggle al window para poder llamarla desde el HTML generado
        window.toggleDescription = handleToggleDescription;
      } else if (typeof data === "string") {
        const formattedResponse = `
          <div style="padding: 8px; color: #333; line-height: 1.4; word-break: break-word; font-size: ${
            isXsMobile ? "0.75rem" : "0.8rem"
          };">
            ${data.replace(/\n/g, "<br/>")}
          </div>
        `;

        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: formattedResponse,
          role: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botResponse]);
      } else {
        throw new Error("Respuesta inválida del servidor");
      }
    } catch (error: any) {
      console.log("Error al comunicarse con el backend:", error.message);

      const fallbackResponses = [
        "¿Podrías darme más detalles? Por ejemplo: 'Busco casa de 3 habitaciones en Heredia'",
        "Para ayudarte mejor, dime qué tipo de propiedad necesitas (casa, apartamento, terreno) y en qué zona",
        "¿Qué características son importantes para ti? (número de habitaciones, baños, parqueo, amenidades)",
        "¿Estás buscando para comprar o alquilar?",
        "No encontré resultados exactos. Prueba con términos como: 'apartamentos en alquiler en San José centro' o 'casas en venta en Alajuela'",
      ];

      const randomResponse =
        fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: `
          <div style="padding: 8px; color: #333; line-height: 1.4; font-size: ${
            isXsMobile ? "0.75rem" : "0.8rem"
          };">
            ${randomResponse}
          </div>
        `,
        role: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);

      if (!isOpenChatBubble) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  }, [
    input,
    isOpenChatBubble,
    expandedDescriptions,
    isXsMobile,
    handleToggleDescription,
    truncateDescription,
    isTextFromSearch,
    searchTerm,
  ]);

  useEffect(() => {
    if (searchTermForAri) {
      setSearchTerm(searchTermForAri);
    }
  }, [searchTermForAri, setSearchTerm]);

  useEffect(() => {
    if (isTextFromSearch) {
      const handleSend = async () => {
        await handleSendMessage();
        setIsTextFromSearch && setIsTextFromSearch(false);
      };
      handleSend();
    }
  }, [isTextFromSearch, handleSendMessage, setIsTextFromSearch]);

  const toggleChat = () => {
    if (setIsOpenChatBubble) {
      setIsOpenChatBubble(!isOpenChatBubble);
    }
    if (!isOpenChatBubble) {
      setUnreadCount(0);
      setShowGreeting(false);
    }
  };

  const chatWidth = isXsMobile ? "280px" : isMobile ? "300px" : "350px";
  const chatHeight = isXsMobile ? "450px" : isMobile ? "480px" : "500px";
  const chatBottom = isMobile ? 160 : 180;
  const chatRight = isMobile ? 8 : 16;

  // Posición del botón de chat
  const fabBottom = isMobile ? 200 : 120;

  // Posición del mensaje de saludo
  const greetingBottom = isMobile ? 200 : 120;
  const greetingRight = 80;

  return (
    <>
      <AnimatePresence>
        {isOpenChatBubble && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              bottom: chatBottom,
              right: chatRight,
              zIndex: 1300,
              filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.3))",
              width: chatWidth,
              height: chatHeight,
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                zIndex: 100,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: "#ffffff",
                position: "relative",
                borderRadius: "16px",
                boxShadow: `
                  0px 4px 8px rgba(0, 0, 0, 0.1),
                  0px 12px 24px rgba(0, 0, 0, 0.15),
                  0px 24px 48px rgba(0, 0, 0, 0.2),
                  0px 0px 0px 1px rgba(0, 0, 0, 0.05)
                `,
                "&:before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "16px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  pointerEvents: "none",
                  zIndex: 1,
                },
                "&:after": {
                  content: '""',
                  position: "absolute",
                  top: "1px",
                  left: "1px",
                  right: "1px",
                  bottom: "1px",
                  borderRadius: "15px",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  pointerEvents: "none",
                  zIndex: 1,
                },
              }}
            >
              <Box
                sx={{
                  p: isXsMobile ? 0.75 : isMobile ? 1 : 1.5,
                  background: "linear-gradient(145deg, #4CAF50, #2E7D32)",
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  position: "relative",
                  zIndex: 2,
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-4px",
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Avatar
                    src={assistantImage}
                    alt="Asistente Virtual"
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography
                    variant={
                      isXsMobile ? "caption" : isMobile ? "body2" : "subtitle2"
                    }
                  >
                    Asistente Virtual
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={toggleChat}
                  sx={{ color: "white", padding: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  p: isXsMobile ? 0.75 : isMobile ? 1 : 1.5,
                  flexGrow: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: isXsMobile ? 0.75 : isMobile ? 1 : 1.5,
                  backgroundColor: "#f5f5f5",
                }}
              >
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        message.role === "user" ? "flex-end" : "flex-start",
                      mb: 0.5,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection:
                          message.role === "user" ? "row-reverse" : "row",
                        alignItems: "flex-start",
                        gap: 0.5,
                        width: "95%", // Aumentamos el porcentaje de ancho
                        maxWidth: "none", // Eliminamos la restricción de maxWidth
                      }}
                    >
                      {message.role === "assistant" && (
                        <Avatar
                          src={assistantImage}
                          alt="Asistente Virtual"
                          sx={{ width: 24, height: 24 }}
                        />
                      )}
                      <Paper
                        sx={{
                          p: isXsMobile ? 0.75 : 1,
                          backgroundColor:
                            message.role === "user" ? "#E3F2FD" : "white",
                          borderRadius: 1.5,
                          width: "100%",
                          wordBreak: "break-word",
                        }}
                      >
                        {message.role === "assistant" ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: message.content,
                            }}
                          />
                        ) : (
                          <Typography
                            variant={isXsMobile ? "caption" : "body2"}
                          >
                            {message.content}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  </Box>
                ))}
                {isLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Avatar
                        src={assistantImage}
                        alt="Asistente Virtual"
                        sx={{ width: 24, height: 24 }}
                      />
                      <CircularProgress size={16} />
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: isXsMobile ? 0.75 : isMobile ? 1 : 1.5,
                    py: 0.5,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mr: 0.5,
                      whiteSpace: "nowrap",
                      fontSize: isXsMobile ? "0.65rem" : "0.7rem",
                    }}
                  >
                    Sugerencias:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      overflowX: "auto",
                      flexWrap: "nowrap",
                      "&::-webkit-scrollbar": {
                        height: "3px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f0f0f0",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: "3px",
                      },
                    }}
                  >
                    <Chip
                      label="Alajuela"
                      size="small"
                      onClick={() => setInput("Propiedades en Alajuela")}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                        fontSize: isXsMobile ? "9px" : "10px",
                        height: isXsMobile ? "18px" : "20px",
                        minWidth: "auto",
                      }}
                    />
                    <Chip
                      label="San José"
                      size="small"
                      onClick={() => setInput("Propiedades en San José")}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                        fontSize: isXsMobile ? "9px" : "10px",
                        height: isXsMobile ? "18px" : "20px",
                        minWidth: "auto",
                      }}
                    />
                    <Chip
                      label="Comprar"
                      size="small"
                      onClick={() => setInput("Propiedades en venta")}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                        fontSize: isXsMobile ? "9px" : "10px",
                        height: isXsMobile ? "18px" : "20px",
                        minWidth: "auto",
                      }}
                    />
                    <Chip
                      label="Alquilar"
                      size="small"
                      onClick={() => setInput("Propiedades en alquiler")}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                        fontSize: isXsMobile ? "9px" : "10px",
                        height: isXsMobile ? "18px" : "20px",
                        minWidth: "auto",
                      }}
                    />
                    <Chip
                      label="Casas"
                      size="small"
                      onClick={() => setInput("Casas en venta o alquiler")}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                        fontSize: isXsMobile ? "9px" : "10px",
                        height: isXsMobile ? "18px" : "20px",
                        minWidth: "auto",
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  component="form"
                  sx={{
                    p: isXsMobile ? 0.75 : isMobile ? 1 : 1.5,
                    backgroundColor: "white",
                    display: "flex",
                    gap: 0.5,
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Escribe tu mensaje..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    variant="outlined"
                    disabled={isLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: isXsMobile ? "0.75rem" : "0.875rem",
                      },
                    }}
                  />

                  <IconButton
                    color="primary"
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    sx={{
                      bgcolor: "#4CAF50",
                      color: "white",
                      "&:hover": { bgcolor: "#3e8e41" },
                      padding: isXsMobile ? 0.5 : 0.75,
                    }}
                  >
                    <SendIcon fontSize={isXsMobile ? "small" : "medium"} />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Burbuja de saludo cuando el chat está cerrado */}
      <Zoom in={showGreeting && !isOpenChatBubble}>
        <Box
          sx={{
            position: "fixed",
            bottom: greetingBottom,
            right: greetingRight,
            zIndex: 1200,
            maxWidth: isXsMobile ? "150px" : isMobile ? "170px" : "200px",
            borderRadius: 2,
            backgroundColor: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            p: isXsMobile ? 1 : isMobile ? 1.25 : 1.5,
            "&:before": {
              content: '""',
              position: "absolute",
              bottom: 20,
              right: -8,
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: "10px solid white",
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "medium",
              fontSize: isXsMobile
                ? "0.65rem"
                : isMobile
                ? "0.7rem"
                : "0.75rem",
            }}
          >
            ¡Hola! Soy ALI, tu asistente virtual de confianza en bienes raíces.
            👽
          </Typography>
        </Box>
      </Zoom>

      <Badge
        badgeContent={unreadCount}
        color="error"
        overlap="circular"
        invisible={unreadCount === 0}
        sx={{
          position: "fixed",
          bottom: fabBottom,
          right: 16,
          zIndex: 1201,
        }}
      >
        <Fab
          color="primary"
          aria-label="chat"
          onClick={toggleChat}
          sx={{
            backgroundColor: "#fff",
            "&:hover": {
               backgroundColor: alpha(theme.palette.primary.main, 0.5),
            },
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
            animation: isOpenChatBubble ? "none" : "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": {
                boxShadow:  `0 0 0 0 ${theme.palette.primary.main}`,
              },
              "70%": {
                boxShadow: "0 0 0 10px rgba(76, 175, 80, 0)",
              },
              "100%": {
                boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
              },
            },
            p: 0,
            overflow: "hidden",
            border: `3px solid ${theme.palette.primary.main}`,
          }}
        >
          <Avatar
            src={assistantImage}
            alt="Asistente Virtual"
            sx={{
              width: isCompanyRoute ? '75%' : '100%',  
              height: isCompanyRoute ? '75%' : '100%', 
              borderRadius: 0,
            }}
          />
        </Fab>
      </Badge>
    </>
  );
};

declare global {
  interface Window {
    toggleDescription: (propertyId: string) => void;
  }
}

export default ChatBubble;
